/** Storybook WorkOrderCanvas look — applied only on factory (vertical) canvases. */

export type FactoryCanvasBackground = {
  gap: number;
  size: number;
  color: string;
  bgColor: string;
};

export type FactoryEdgeTone = "default" | "running" | "failed";

export type FactoryEdgePalette = {
  default: { stroke: string; strokeWidth: number };
  running: { stroke: string; strokeWidth: number };
  failed: { stroke: string; strokeWidth: number };
};

export function factoryCanvasBackground(isDark: boolean, isEditing = false): FactoryCanvasBackground {
  const size = isEditing ? 3 : 1;
  if (isDark) {
    return { gap: 22, size, color: "#33312b", bgColor: "#14120b" };
  }
  return { gap: 22, size, color: "#b8c4d0", bgColor: "#e2e8f0" };
}

/** Light-mode factory wires. Darker than the canvas dots; used in edit and view. */
export const FACTORY_EDGE_STROKE = "#94a3b8";
export const FACTORY_EDGE_STROKE_WIDTH = 2;

export function factoryEdgePalette(isDark: boolean): FactoryEdgePalette {
  const strokeWidth = FACTORY_EDGE_STROKE_WIDTH;
  if (isDark) {
    return {
      default: { stroke: "#4a4740", strokeWidth },
      running: { stroke: "#818cf8", strokeWidth },
      failed: { stroke: "#f87171", strokeWidth },
    };
  }
  return {
    default: { stroke: FACTORY_EDGE_STROKE, strokeWidth },
    running: { stroke: "#818cf8", strokeWidth },
    failed: { stroke: "#fca5a5", strokeWidth },
  };
}

/** Map node event state → edge tone (target node drives the edge, like Storybook). */
export function resolveFactoryEdgeTone(eventState: string | undefined): FactoryEdgeTone {
  switch (eventState) {
    case "failed":
    case "error":
      return "failed";
    case "running":
    case "cancelling":
      return "running";
    // queued / cancelled / triggered / success stay default slate
    default:
      return "default";
  }
}

export function factoryEdgeToneClassName(tone: FactoryEdgeTone): string | undefined {
  if (tone === "running") return "sp-edge-factory-running";
  if (tone === "failed") return "sp-edge-factory-failed";
  return undefined;
}

/** Read primary event state from canvas block node data (component or trigger). */
export function primaryEventStateFromCanvasNodeData(data: unknown): string | undefined {
  if (!data || typeof data !== "object") return undefined;

  const record = data as Record<string, unknown>;
  for (const key of ["component", "trigger"] as const) {
    const block = record[key];
    if (!block || typeof block !== "object") continue;

    const sections = (block as { eventSections?: Array<{ eventState?: unknown }> }).eventSections;
    const state = sections?.[0]?.eventState;
    if (typeof state === "string" && state.length > 0) return state;
  }

  return undefined;
}

/** Small square ports matching Storybook factory nodes. */
export const FACTORY_HANDLE_STYLE = {
  width: 10,
  height: 10,
  borderRadius: 3,
  border: "1px solid var(--border, #e5e7eb)",
  background: "var(--card, #ffffff)",
  boxShadow: "none",
  // Half-in ports must paint above the card body.
  zIndex: 11,
} as const;

/** Keep FactoryNodeCard, append ghost, placement gap, and ELK estimates aligned. */
export const FACTORY_NODE_CARD_WIDTH = 280;
export const FACTORY_NODE_CARD_HEIGHT = 104;
export const FACTORY_NODE_STEP_CARD_WIDTH = 320;
const FACTORY_NODE_STEP_ROW_HEIGHT = 28;
const FACTORY_NODE_STEP_LIST_PADDING = 20;

export function factoryNodeCardSize(stepCount = 0): { width: number; height: number } {
  if (stepCount <= 0) {
    return { width: FACTORY_NODE_CARD_WIDTH, height: FACTORY_NODE_CARD_HEIGHT };
  }
  return {
    width: FACTORY_NODE_STEP_CARD_WIDTH,
    height: FACTORY_NODE_CARD_HEIGHT + FACTORY_NODE_STEP_LIST_PADDING + stepCount * FACTORY_NODE_STEP_ROW_HEIGHT,
  };
}
/** Vertical gap below a factory card in persisted ELK layout, live, and run. */
export const FACTORY_NODE_VERTICAL_GAP = 64;
/**
 * Edit/configure only. Compact 64px leaves node duplicate/delete on top of
 * the edge-delete control. Live and run keep FACTORY_NODE_VERTICAL_GAP.
 */
export const FACTORY_NODE_EDIT_VERTICAL_GAP = 200;
